/**
 * Tracking anonyme côté serveur — événements fire-and-forget.
 *
 * On envoie 3 types d'événements (partie démarrée / question répondue / partie
 * terminée) à `POST /api/tracking`, identifiés par la session anonyme (jamais
 * un compte). Le serveur stocke et agrège (backoffice). Aucun échec ne doit
 * jamais bloquer le jeu → tout est avalé silencieusement.
 */

import { classifyArchetypeKey } from "./archetypes";
import { classifyCourant } from "./courant";
import { deriveAggregates } from "./types";
import type { WorldState } from "./types";

type TrackEvent =
  "game_started" | "decision_made" | "mandate_ended" | "survey_feedback";

export function track(
  sessionId: string,
  eventType: TrackEvent,
  payload: Record<string, unknown>,
): void {
  void fetch("/api/tracking", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      session_id: sessionId,
      event_type: eventType,
      payload,
    }),
  }).catch(() => {
    /* analytics best-effort — ne jamais perturber la partie */
  });
}

/** Partie terminée : profil politique qui se dégage + contexte de fin. */
export function trackMandateEnded(
  sessionId: string,
  state: WorldState,
  countryArchetype: string,
  // Promesses du contrat de campagne : ids + progrès final (analyse
  // backoffice — quelle promesse, tenue ou trahie). Ajout 2026-07-15.
  pledges: { id: string; progress: number }[] = [],
): void {
  const p = state.polity.profile;
  const agg = deriveAggregates(state);
  const debt =
    (state.economy.public_debt / Math.max(state.economy.gdp, 1)) * 100;
  track(sessionId, "mandate_ended", {
    archetype: countryArchetype,
    archetype_key: classifyArchetypeKey(p),
    courant: classifyCourant(p),
    cause: state.polity.mandate_end_cause ?? "term",
    turns: state.turn,
    profile: {
      economic: p.economic,
      societal: p.societal,
      international: p.international,
      authority: p.authority,
    },
    approval: state.polity.approval_rate ?? agg.meanSatisfaction,
    satisfaction: agg.meanSatisfaction,
    debt_to_gdp: debt,
    pledges: pledges.map((p) => ({
      id: p.id,
      progress: Math.round(p.progress * 100) / 100,
    })),
  });
}

/** Les 6 dimensions du questionnaire beta (échelle 1..5). */
export interface SurveyRatings {
  interest?: number;
  themes?: number;
  mechanics?: number;
  objectives?: number;
  satisfaction?: number;
  replay?: number;
}

/** Questionnaire beta — ressenti déclaré du joueur, stocké dans la table
 *  dédiée `survey_feedback` (POST /api/feedback). Fire-and-forget. */
export function submitFeedback(
  sessionId: string,
  ratings: SurveyRatings,
): void {
  void fetch("/api/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ session_id: sessionId, ...ratings }),
  }).catch(() => {
    /* best-effort — ne jamais perturber le jeu */
  });
}
